import { useState, useCallback } from 'react'
import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { TrashIcon, ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface InvoiceRow {
  id: number
  number: string | null
  order_id: number | null
  order_po_number: string | null
  customer_name: string | null
  issue_date: string | null
  state: string
  amount: number
  total_amount: number
  format: string
}

interface InvoicesIndexProps {
  invoices: InvoiceRow[]
  states: [string, string][]
  templates: [string, string][]
  default_template: string
}

// ─── Helpers ────────────────────────────────────

function formatCurrency(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(d: string | null): string {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}`
}

const STATE_COLORS: Record<string, string> = {
  pending: 'yellow', on_hold: 'zinc', paid: 'emerald', overdue: 'red', canceled: 'zinc',
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function InvoicesIndex({ invoices: initialInvoices, states, templates, default_template }: InvoicesIndexProps) {
  const [invoices, setInvoices] = useState(initialInvoices)
  const [exportTemplates, setExportTemplates] = useState<Record<number, string>>({})
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  // ─── State update ───────────────────────────
  const updateState = useCallback(async (row: InvoiceRow, newState: string) => {
    try {
      const res = await fetch(`/invoices/${row.id}.json`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ invoice: { state: newState } }),
      })
      if (res.ok) {
        setInvoices(prev => prev.map(inv => inv.id === row.id ? { ...inv, state: newState } : inv))
        flash(row.id, 'success')
        notify('Saved:State updated')
      } else { flash(row.id, 'error') }
    } catch { flash(row.id, 'error') }
  }, [csrfToken, flash, notify])

  // ─── Delete ─────────────────────────────────
  const deleteInvoice = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this invoice?')) return
    try {
      const res = await fetch(`/invoices/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setInvoices(prev => prev.filter(inv => inv.id !== id)); notify('Deleted:Invoice removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Options ────────────────────────────────
  const stateOptions = states.map(([val, label]) => ({ value: val, label }))
  const templateOptions = templates.map(([label, val]) => ({ value: val, label }))
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))

  // ─── Pagination ─────────────────────────────
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(invoices)

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<InvoiceRow>[] = [
    {
      key: 'number', label: 'Invoice #',
      render: row => (
        <a href={`/invoices/${row.id}.pdf?template=${default_template}`} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {row.number || `#${row.id}`}
        </a>
      ),
      sortValue: row => row.number || '',
      filterValue: row => row.number || '',
    },
    {
      key: 'order_po_number', label: 'Order #',
      render: row => row.order_id
        ? <Link href={`/inertia_customer_orders/${row.order_id}`} className="text-indigo-600 hover:underline dark:text-indigo-400">{row.order_po_number || `#${row.order_id}`}</Link>
        : <span className="text-zinc-400">—</span>,
      sortValue: row => row.order_po_number || '',
      filterValue: row => row.order_po_number || '',
    },
    {
      key: 'customer_name', label: 'Customer',
      render: row => <>{row.customer_name || '—'}</>,
      sortValue: row => row.customer_name || '',
      filterValue: row => row.customer_name || '',
    },
    {
      key: 'issue_date', label: 'Date',
      render: row => <>{formatDate(row.issue_date)}</>,
      sortValue: row => row.issue_date || '',
      filterValue: row => row.issue_date || '',
    },
    {
      key: 'state', label: 'State',
      render: row => (
        <Badge color={(STATE_COLORS[row.state] || 'zinc') as 'zinc'}>
          {row.state.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </Badge>
      ),
      sortValue: row => row.state,
      filterValue: row => row.state,
      editSelect: {
        options: stateOptions,
        getValue: row => row.state,
        onSelect: (row, val) => updateState(row, val),
      },
    },
    {
      key: 'amount', label: 'Amount',
      render: row => <span className="font-semibold">{formatCurrency(row.amount)}</span>,
      sortValue: row => row.amount,
    },
    {
      key: 'export', label: 'Export', noFilter: true,
      render: row => (
        <div className="flex items-center gap-1">
          <div className="w-28">
            <FormSelect value={exportTemplates[row.id] || default_template} onChange={v => setExportTemplates(prev => ({ ...prev, [row.id]: v }))} options={templateOptions} />
          </div>
          <Button variant="secondary" size="xs" title="Export PDF" onClick={() => window.open(`/invoices/${row.id}.pdf?template=${exportTemplates[row.id] || default_template}`, '_blank')}>
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ]

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="Invoice Archive" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <PageHeader title="Invoice Archive" subtitle="Invoices" icon={<DocumentTextIcon className="h-5 w-5" />} actions={
        <div className="w-36">
          <FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} />
        </div>
      } />

      <div className="mb-2">
        <Badge color="zinc"><span className="text-xs font-medium">{totalCount} invoices</span></Badge>
      </div>

      <DataTable
        rows={paginatedRows}
        columns={columns}
        rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null}
        flashStatus={flashRow?.status ?? null}
        emptyText="No invoices"
        filteredEmptyText="No invoices match your filters"
        actions={row => (
          <button onClick={() => deleteInvoice(row.id)} className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400" title="Delete invoice">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />
    </>
  )
}

InvoicesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
